import { FunctionComponent, useEffect, useState } from 'react';
import { ProductConfiguration } from '@/models/product';
import {
    BulkTagMode,
    CACHE_KEY_BULK_UPDATES,
    usePostBulkUpdatesMutation,
} from '@/api/v2/productConfigurations';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import {
    BulkUpdateActionPayload,
    BulkUpdateType,
    ConfigurationCollection,
    ConfigurationState,
    initialConfigurationState,
} from '@/models/bulkUpdate';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import { DateRange } from '@/models/general';
import { useConfigurationPanelContext } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { ConfigurationsPanel } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationsPanel';

const plainIntegrationObject = () => ({
    barcode: false,
    internalProduction: false,
    keycard: false,
    pickup: false,
    mobileFlow: false,
});

export const initialBookingOptions = {
    integrationConfiguration: plainIntegrationObject(),
    portalConfiguration: plainIntegrationObject(),
    b2bConfiguration: plainIntegrationObject(),
    hospitalityConfiguration: plainIntegrationObject(),
};

type ConfigurationsPanelContainerProps = {
    selectedConfigurations: ProductConfiguration[];
};

export const ConfigurationsPanelContainer: FunctionComponent<
    ConfigurationsPanelContainerProps
> = ({ selectedConfigurations }) => {
    // liftOperatorData is null until the stored session is rehydrated, see FilterTagsPage.
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;

    // prefetch for components inside panel
    useGetLiftOperatorQuery({ liftOperatorId }, { skip: !liftOperatorId });

    const { addNotification } = useNotification();
    const [postBulkUpdate, { isLoading }] = usePostBulkUpdatesMutation({
        fixedCacheKey: CACHE_KEY_BULK_UPDATES,
    });
    const [configurationState, setConfigurationState] =
        useState<ConfigurationState>(initialConfigurationState);
    const [bookingConfiguration, setBookingConfiguration] =
        useState<ConfigurationCollection>(null);
    const [validityConfiguration, setValidityConfiguration] =
        useState<DateRange>(null);
    const [startDateValidityConfiguration, setStartDateValidityConfiguration] =
        useState<DateRange>(null);
    const [selectedConditionLinkIds, setSelectedConditionLinkIds] = useState<
        string[]
    >([]);
    const [removeConditions, setRemoveConditions] = useState<boolean>(null);
    const [isActive, setIsActive] = useState<boolean>(null);
    const [filterTagMode, setFilterTagMode] = useState<BulkTagMode | null>(
        null
    );
    const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
    const [tagMode, setTagMode] = useState<BulkTagMode | null>(null);
    const [tagIds, setTagIds] = useState<string[]>([]);

    const { setIsActive: setPanelActive } = useConfigurationPanelContext();

    const onPanelChange = (action: BulkUpdateActionPayload) => {
        switch (action.type) {
            case BulkUpdateType.ACTIVE_STATUS:
                setIsActive(action.isActive);
                break;
            case BulkUpdateType.SET_DYNAMIC_PRICING:
                setSelectedConditionLinkIds(action.conditionLinkIds);
                break;
            case BulkUpdateType.SET_REMOVE_CONDITION:
                setRemoveConditions(action.payload);
                break;
            case BulkUpdateType.SET_BOOKING_OPTIONS:
                setBookingConfiguration(action.configurations);
                break;
            case BulkUpdateType.SET_VALIDITY_OPTIONS:
                setValidityConfiguration(action.payload);
                break;
            case BulkUpdateType.SET_START_DATE_VALIDITY_OPTIONS:
                setStartDateValidityConfiguration(action.payload);
                break;
            case BulkUpdateType.SET_FILTER_TAGS:
                setFilterTagMode(action.mode);
                setFilterTagIds(action.filterTagIds);
                break;
            case BulkUpdateType.SET_TAGS:
                setTagMode(action.mode);
                setTagIds(action.tagIds);
                break;
        }
    };

    const onSave = () => {
        if (!selectedConfigurations?.length) return;

        postBulkUpdate({
            productIds: selectedConfigurations.map((sC) => sC.productId),
            liftOperatorId: liftOperatorId,
            enabled: isActive,
            conditionLinkIds: selectedConditionLinkIds,
            removeConditionLinks: removeConditions,
            ...(bookingConfiguration ? bookingConfiguration : {}),
            validFrom: validityConfiguration?.[0] || null,
            validTo: validityConfiguration?.[1] || null,
            startDateValidFrom: startDateValidityConfiguration?.[0] || null,
            startDateValidTo: startDateValidityConfiguration?.[1] || null,
            removeValidFromTo: configurationState.setRemoveValidFromTo,
            removeStartDateFromTo:
                configurationState.setRemoveStartDateValidFromTo,
            filterTagMode: filterTagMode ?? undefined,
            filterTagIds: filterTagMode ? filterTagIds : undefined,
            tagMode: tagMode ?? undefined,
            tagIds: tagMode ? tagIds : undefined,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Konfigurationen erfolgreich aktualisiert',
                });
                setConfigurationState(initialConfigurationState);
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Aktualisieren. Versuchen Sie es später noch einmal',
                });
            });
    };

    useEffect(() => {
        const hasSelection = !!selectedConfigurations?.length;
        setPanelActive(hasSelection);

        // The panel is hidden by a Grow transition, not unmounted, so its state survives.
        // Without this reset the next selection would inherit the previous bulk action.
        if (!hasSelection) {
            setConfigurationState(initialConfigurationState);
        }
    }, [selectedConfigurations]);

    return (
        <ConfigurationsPanel
            isActive={isActive}
            removeConditions={removeConditions}
            selectedConditionLinkIds={selectedConditionLinkIds}
            numOfConfigurations={selectedConfigurations.length}
            configurationCollection={bookingConfiguration}
            onConfigurationsChange={onPanelChange}
            configurationState={configurationState}
            onConfigurationStateChange={(nextState) => {
                setConfigurationState(nextState);
            }}
            validityRange={validityConfiguration || [null, null]}
            startDateValidityRange={
                startDateValidityConfiguration || [null, null]
            }
            onSave={onSave}
            isLoading={isLoading}
        />
    );
};
